import React, { useState, useEffect, useCallback } from 'react';
import './styles.css';
import { useNavigate, useLocation } from 'react-router-dom';
import checkEncuesta from '../../../static/img/checkEncuesta.svg';
import back from '../../../static/img/back.svg';
import Footer from '../footer/footer';
import BloqueCard from '../../../static/assets/cards/bloquecCard';
import {
  fetchBloques,
  fetchEncuesta,
  getUserInfo,
} from '../../../services/deportistaApi';
import { getUserId, sendNotification } from '../../../services/utilsApi';
import LoadingScreen from '../../../static/assets/loadingScreen/loadingScreen';
import CuadroTexto from '../../../static/assets/cuadroTexto/cuadroTexto';
import { trackEvent } from '../../../utils/newrelic_utils';

const Entrenamiento = () => {
  const navigate = useNavigate();
  const location = useLocation()
  const today = new Date();
  const initialDate = location.state?location.state:today
  const [userId, setUserId] = useState(null);
  const [trainerId, setTrainerId] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [bloques, setBloques] = useState([]);
  const [encuestas, setEncuestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prevLoading, setPrevLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [notifiedDate, setNotifiedDate] = useState(null);

  const getFormattedDate = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate())
      .toISOString()
      .split('T')[0];

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { user_id },
        } = await getUserId();
        setUserId(user_id);
        const {
          data: { perfil },
        } = await getUserInfo(user_id);
        setTrainerId(perfil?.entrenador?.id);
        setPerfil(perfil);
      } catch (error) {
        trackEvent('Entrenamiento_GetUserInfo_Error', true, error.message, {
          module: 'Entrenamiento',
          fn: 'getUserInfo',
        });
      }
    })();
  }, []);

  const allExercisecompleted = bloques.every((bloqueItem) =>
    bloqueItem.ejercicios.every(
      (ejercicio) => ejercicio.completado
    )
  )

  const fetchData = useCallback(
    async (date) => {
      if (!userId) return;
      setLoading(true);
      try {
        const fecha = getFormattedDate(date);
        const bloquesRes = await fetchBloques(userId, fecha);

        setBloques(
          (bloquesRes.data.bloques_asignados || []).map((bloqueItem) => ({
            titulo: bloqueItem.nombre,
            subtitulo: bloqueItem.descripcion,
            ejercicios: bloqueItem.ejercicios.map((ejercicio) => {
              const reps = ejercicio.series.map((serie) => serie.repeticiones);
              const resumen =
                reps.length > 0
                  ? reps.every((v) => v === reps[0])
                    ? `${reps.length} x ${reps[0]}`
                    : `${reps.length} x ${reps.join(',')}`
                  : '';
              return {
                id_ejercicio_data: ejercicio.id_ejercicio_data,
                id_ejercicio: ejercicio.id_ejercicio,
                nombre: ejercicio.nombre,
                series: ejercicio.series.map((serie) => ({
                  id_serie: serie.id_serie,
                  serie: serie.serie,
                  repeticiones: serie.repeticiones,
                  peso: serie.peso,
                  porciento: serie.porciento,
                  completado: serie.completado,
                })),
                completado: ejercicio.completado,
                subgrupo: ejercicio.subgrupo,
                subgrupo_orden: ejercicio.subgrupo_orden,
                notas_entrenador:
                  ejercicio.notas_entrenador,
                resumenSeries: resumen,
              };
            }),
          }))
        );

        const encuestasRes = await fetchEncuesta(userId, fecha);
        setEncuestas(
          (encuestasRes.data.encuestas || []).map((encuestaItem) => ({
            id_encuesta_deportista: encuestaItem.id_encuesta_deportista,
            al_inicio: encuestaItem.al_inicio,
            nombre_encuesta: encuestaItem.nombre_encuesta,
          }))
        );
      } catch (error) {
        setErrorMessage('Hubo un error al cargar los entrenamientos.');
        setBloques([]);
        setEncuestas([]);
        trackEvent('Entrenamiento_FetchData_Error', true, error.message, {
          module: 'Entrenamiento',
          fn: 'fetchData',
          date,
        });
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate, fetchData]);

  useEffect(() => {
    setNotifiedDate(null);
  }, [selectedDate]);

  useEffect(() => {
    if (prevLoading && !loading) {
      const fecha = getFormattedDate(selectedDate);
      const hayBloques = bloques.length > 0;
      const todosCompletos =
        hayBloques &&
        bloques.every((bloqueItem) =>
          bloqueItem.ejercicios.every((ejercicio) => ejercicio.completado)
        );

      if (
        userId &&
        trainerId &&
        hayBloques &&
        todosCompletos &&
        notifiedDate !== fecha
      ) {
        sendNotification({
          id_desde: userId,
          id_hasta: trainerId,
          asunto: 'Rutina completada',
          mensaje: `${perfil?.nombre} ${perfil?.apellido} ha completado su entrenamiento del ${fecha}.`,
          id_tipo: 1,
          visto: false,
          created_at: `${fecha}T00:00:00Z`,
        })
          .then(() => setNotifiedDate(fecha))
          .catch((error) => {
            trackEvent(
              'Entrenamiento_SendNotification_Error',
              true,
              error.message,
              { module: 'Entrenamiento', fn: 'sendNotification', fecha }
            );
          });
      }
    }
    setPrevLoading(loading);
  }, [
    loading,
    prevLoading,
    bloques,
    userId,
    trainerId,
    notifiedDate,
    selectedDate,
    perfil?.nombre,
    perfil?.apellido,
  ]);

  const toggleCompleto = (bloqueIndex, ejercicioIndex) => {
    setBloques((prevBloques) =>
      prevBloques.map((bloqueItem, bloqueIdx) =>
        bloqueIdx === bloqueIndex
          ? {
              ...bloqueItem,
              ejercicios: bloqueItem.ejercicios.map((ejercicio, ejerIdx) =>
                ejerIdx === ejercicioIndex
                  ? { ...ejercicio, completado: !ejercicio.completado }
                  : ejercicio
              ),
            }
          : bloqueItem
      )
    );
  };

  const generateWeekDays = (selectedDate) => {
    const weekDays = [];
    for (let i = -3; i <= 3; i++) {
      const day = new Date(selectedDate);
      day.setDate(day.getDate() + i);
      weekDays.push({
        date: day,
        dayOfWeekInitial: day.toLocaleDateString("es-ES", { weekday: "short" }).charAt(0),
        dayOfMonth: day.getDate(),
        isToday: day.toDateString() === today.toDateString(),
        isSelected: getFormattedDate(day) === getFormattedDate(selectedDate),
      });
    }
    return weekDays;
  };

  const weekDays = generateWeekDays(selectedDate);
  const offsetIndex = 3 - weekDays.findIndex((dayObj) => dayObj.isSelected);
  const reorderedWeekDays = weekDays.map((_, idx) => {
    const newIndex = (idx - offsetIndex + 7) % 7;
    return weekDays[newIndex];
  });

  const handleDayClick = (dayObj) => setSelectedDate(dayObj);
  const handleBackToToday = () => setSelectedDate(today);
  const handleBack = () => navigate('/dashboard');

  if (loading) return <LoadingScreen />;

  return (
    <>
      {errorMessage && (
        <CuadroTexto
          message={errorMessage}
          onAccept={() => setErrorMessage('')}
          showCancel={false}
        />
      )}
      <div className="entrenamiento">
        <div className="entrenamiento-encabezado">
          <button className="back-button-entrenamiento" onClick={handleBack}>
            <img src={back} alt="back" />
          </button>
          <h3 className="entrenamiento-titulo">Entrenamiento</h3>
          {getFormattedDate(selectedDate) !== getFormattedDate(today) ? (
            <button className="volver-hoy" onClick={handleBackToToday}>
              Hoy
            </button>
          ) : (
            <div className="volver-hoy hidden" />
          )}
        </div>
        <div className="contenedor-semana" role="list">
          {reorderedWeekDays.map((dayObj, dayIdx) => (
            <div
              key={dayIdx}
              className={`dia ${dayObj.isSelected ? 'dia-seleccionado' : ''}`}
              role="listitem"
              onClick={() => handleDayClick(dayObj.date)}
            >
              <div className="dia-letra">{dayObj.dayOfWeekInitial}</div>
              <div className="dia-numero">{dayObj.dayOfMonth}</div>
            </div>
          ))}
        </div>
        <div className="entrenamiento-linea-header" />
        {bloques.length > 0 ? (
          <div className="bloque-container">
            {encuestas
              .filter((encuestaItem) => encuestaItem.al_inicio)
              .map((encuestaItem) => (
                <div
                  key={encuestaItem.id_encuesta_deportista}
                  className="entrenamiento-wellness-container"
                  onClick={() =>
                    navigate(
                      `/encuesta/${encuestaItem.id_encuesta_deportista}/${getFormattedDate(
                        selectedDate
                      )}`
                    )
                  }
                  role="button"
                  aria-label={`Ir a la encuesta ${encuestaItem.nombre_encuesta}`}
                >
                  <div className="entrenamiento-wellness-texto">
                    {encuestaItem.nombre_encuesta}
                  </div>
                  <div className="entrenamiento-rectangulo-encuesta" />
                  <div className="entrenamiento-texto-encuesta">Encuesta</div>
                  <img
                    className="entrenamiento-checkEncuesta-icono"
                    alt="CheckEncuesta"
                    src={checkEncuesta}
                  />
                </div>
              ))}
            {bloques.map((bloqueItem, bloqueIdx) => (
              <BloqueCard
                key={bloqueIdx}
                bloqueIndex={bloqueIdx + 1}
                titulo={bloqueItem.titulo}
                ejercicios={bloqueItem.ejercicios}
                isCompleted={bloqueItem.completado}
                toggleCompleto={() => toggleCompleto(bloqueIdx)}
                selectedDay={selectedDate}
                bloqueCompleto={bloqueItem}
              />
            ))}

            {encuestas
              .filter((encuestaItem) => !encuestaItem.al_inicio && allExercisecompleted)
              .map((encuestaItem) => (
                <div
                  key={encuestaItem.id_encuesta_deportista}
                  className="entrenamiento-wellness-container"
                  onClick={() =>
                    navigate(
                      `/encuesta/${encuestaItem.id_encuesta_deportista}/${getFormattedDate(
                        selectedDate
                      )}`
                    )
                  }
                  role="button"
                  aria-label={`Ir a la encuesta ${encuestaItem.nombre_encuesta}`}
                >
                  <div className="entrenamiento-wellness-texto">
                    {encuestaItem.nombre_encuesta}
                  </div>
                  <div className="entrenamiento-rectangulo-encuesta" />
                  <div className="entrenamiento-texto-encuesta">Encuesta</div>
                  <img
                    className="entrenamiento-checkEncuesta-icono"
                    alt="CheckEncuesta"
                    src={checkEncuesta}
                  />
                </div>
              ))}
              
            <div
              className={
                  allExercisecompleted
                  ? 'completed-div-complete'
                  : 'completed-div-incomplete'
              }
            >
              <span
                className={
                    allExercisecompleted
                    ? 'completed-text-complete'
                    : 'completed-text-incomplete'
                }
              >
                Completado
              </span>
            </div>

          </div>
        ) : (
          <div className="empty-bloque-container">
            <span>No hay entrenamiento programado</span>
          </div>
        )}
      </div>
      <Footer activeScreen="entrenamiento" />
    </>
  );
};

export default Entrenamiento;
